/**
 * Quote Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  FileEdit,
  Hammer,
  DollarSign,
  Truck,
  FileUp,
  FileText,
  Calculator,
  TrendingUp,
  Bot,
  Settings,
  Briefcase,
  Users,
  Mail,
  Plus,
  Receipt,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'
import { getQuoteOverlay } from '@/lib/overlay/quoteOverlay'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'quote' as HubId,
  name: 'Quote Hub',
  icon: FileEdit,
  // Branding tokens resolve through the tenant overlay seam (Track 2.1a-ii).
  // DEFAULTS are amber (unchanged) until a tenant registers via setQuoteOverlay().
  accentColor: getQuoteOverlay().branding.accentColor,
  accentGradient: getQuoteOverlay().branding.accentGradient,
  baseUrl: 'https://quote.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
  },
  {
    id: 'quotes',
    label: 'Quotes',
    icon: FileEdit,
    href: '/quotes',
  },
  {
    id: 'builder',
    label: 'Builder',
    icon: Hammer,
    href: '/builder/new',
  },
  {
    id: 'pricelist',
    label: 'Pricelist',
    icon: DollarSign,
    href: '/pricelist',
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: Truck,
    href: '/suppliers',
  },
  {
    id: 'supplier-quotes',
    label: 'Supplier Quotes',
    icon: FileUp,
    href: '/supplier-quotes',
  },
  // Backend claims app enabled by Jobs Hub GAP W1 (be!4394) — /api/claims/
  // is live with migrations, org scoping, and the claims-page contract.
  {
    id: 'claims',
    label: 'Payment Claims',
    icon: Receipt,
    href: '/claims',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'templates',
    label: 'Templates',
    icon: FileText,
    href: '/templates',
  },
  {
    id: 'formulas',
    label: 'Formula Registry',
    icon: Calculator,
    href: '/formulas',
  },
  {
    id: 'margins',
    label: 'Margin Analysis',
    icon: TrendingUp,
    href: '/margins',
  },
  {
    id: 'ai',
    label: 'AI Suggestions',
    icon: Bot,
    href: '/ai',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('quote')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-quote',
    label: 'New Quote',
    icon: Plus,
    action: () => { window.location.href = '/builder/new' },
    shortcut: '⌘⇧Q',
  },
  {
    id: 'upload-supplier-quote',
    label: 'Upload Supplier Quote',
    icon: FileUp,
    action: () => { window.location.href = '/supplier-quotes/upload' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-quote',
    label: 'New Quote',
    description: 'Create a new quote in the builder',
    icon: Plus,
    action: () => { window.location.href = '/builder/new' },
    keywords: ['create', 'new', 'quote', 'builder'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-quotes',
    label: 'Go to Quotes',
    icon: FileEdit,
    action: () => { window.location.href = '/quotes' },
    keywords: ['navigate', 'quotes'],
    category: 'navigation',
  },
  {
    id: 'go-pricelist',
    label: 'Go to Pricelist',
    icon: DollarSign,
    action: () => { window.location.href = '/pricelist' },
    keywords: ['navigate', 'pricelist', 'prices'],
    category: 'navigation',
  },
  {
    id: 'go-suppliers',
    label: 'Go to Suppliers',
    icon: Truck,
    action: () => { window.location.href = '/suppliers' },
    keywords: ['navigate', 'suppliers'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-crm',
    label: 'Switch to CRM Hub',
    icon: Users,
    action: () => { window.location.href = 'https://crm.quotech.ai' },
    keywords: ['hub', 'crm', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-email',
    label: 'Switch to Email Hub',
    icon: Mail,
    action: () => { window.location.href = 'https://email.quotech.ai' },
    keywords: ['hub', 'email', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS
